import { Form } from 'antd';
import authApi from 'api/authApi';
import { useAppDispatch, useAppTranslation } from 'app/hooks';
import VerticalForm from 'components/Form/VerticalForm';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { RegisterFormRequestModel } from 'models/auth';
import React, { useEffect, useRef, useState } from 'react';
import TextField from '../../../components/FormField/TextField';
import { MdEmail } from 'react-icons/md';
import FieldError from 'components/FormField/FieldError';
import { HiMiniIdentification } from 'react-icons/hi2';
import { FaLock } from 'react-icons/fa';
import { onChangeAuthPage } from 'features/home/homeSlice';
import PasswordField from 'components/FormField/PasswordField';
import { BsCodeSlash } from 'react-icons/bs';
import ReCAPTCHA from 'react-google-recaptcha';
import env from 'app/env';
import { GoogleOAuthProvider } from '@react-oauth/google';
import GoogleAuth from '../sso/GoogleAuth';
import TwitterAuth from '../sso/TwitterAuth';
import AppleAuth from '../sso/AppleAuth';

interface SignUpPageProps {
  isResetFields?: boolean;
  onChangeLoadingStatus: (value: boolean) => void;
}

const SignUpPage: React.FunctionComponent<SignUpPageProps> = ({
  isResetFields = false,
  onChangeLoadingStatus,
}) => {
  const [form] = Form.useForm();
  const t = useAppTranslation();
  const handleResponseError = useHandleResponseError();
  const dispatch = useAppDispatch();
  const refCapcha = useRef<ReCAPTCHA>(null);
  const [haveError, setHaveError] = useState<boolean>(false);
  const [areAllFieldsFilled, setAreAllFieldsFilled] = useState<boolean>(false);
  const [isValidateCapcha, setIsValidateCapcha] = useState<boolean>(false);

  const onSubmit = async (data: RegisterFormRequestModel) => {
    const { confirmPassword, ...rest } = data;

    if (rest.referralCode === undefined) {
      rest.referralCode = '';
    }

    if (rest.promotionCode === undefined) {
      rest.promotionCode = '';
    }

    onChangeLoadingStatus(true);

    const { ok, error } = await authApi.register(rest);

    onChangeLoadingStatus(false);

    if (ok) {
      const onOk = () => {
        form.resetFields();
        dispatch(onChangeAuthPage(0));
      };

      showSuccessModal({
        title: t('register.success.title'),
        content: t('register.success.message'),
        onOk,
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const handleFieldsChange = () => {
    setHaveError(form.getFieldsError().some((error) => error.errors.length));
    const { referralCode, promotionCode, ...rest } = form.getFieldsValue();
    setAreAllFieldsFilled(Object.values(rest).every((value) => Boolean(value)));
  };

  useEffect(() => {
    setIsValidateCapcha(Boolean(refCapcha.current?.getValue()));
  }, [refCapcha.current?.getValue()]);

  useEffect(() => {
    form.resetFields();
  }, [isResetFields, form]);

  useEffect(() => {
    if (form.getFieldValue('confirmPassword')) {
      form.validateFields(['confirmPassword']);
    }
  }, [form.getFieldValue('password')]);

  return (
    <VerticalForm
      className="auth__signup"
      name="signup"
      form={form}
      onFinish={onSubmit}
      onFieldsChange={handleFieldsChange}
    >
      <TextField
        name="email"
        label={t('register.form.email')}
        placeholder={t('placeholder.enterEmail')}
        prefix={<MdEmail />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.email.required')} />,
          },
          {
            type: 'email',
            message: <FieldError message={t('errors.email.invalid')} />,
          },
        ]}
      />
      <PasswordField
        label={t('register.form.password')}
        name="password"
        placeholder={t('placeholder.enterPassword')}
        prefix={<FaLock />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.password.required')} />,
          },
          {
            min: 8,
            message: <FieldError message={t('errors.password.invalid')} />,
          },
          {
            max: 50,
            message: <FieldError message={t('errors.password.invalid')} />,
          },
        ]}
      />
      <PasswordField
        label={t('register.form.confirm')}
        name="confirmPassword"
        placeholder={t('placeholder.confirmPassword')}
        prefix={<FaLock />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('register.form.passwordRequired')} />,
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(<FieldError message={t('register.form.passwordNotMatch')} />);
            },
          }),
        ]}
      />
      <TextField
        label={t('register.form.rereferral')}
        name="referralCode"
        placeholder={t('placeholder.enterReferralCode')}
        prefix={<BsCodeSlash />}
        className="input_primary"
      />
      <TextField
        label={t('register.form.promotion')}
        name="promotionCode"
        placeholder={t('placeholder.enterPromotionCode')}
        prefix={<BsCodeSlash />}
        className="input_primary"
      />
      {areAllFieldsFilled && !haveError && (
        <div className="capcha">
          <ReCAPTCHA ref={refCapcha} sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA || ''} />
        </div>
      )}
      <div className="login__footer">
        <button
          disabled={haveError || !isValidateCapcha}
          type="submit"
          className={`btn-submit ${haveError || !isValidateCapcha ? 'disabled' : ''}`}
        >
          {t('signUp')}
        </button>
      </div>
      <div className="auth-form__footer">
        <div className="divider-text">
          <span>OR</span>
        </div>
      </div>
      <div className="social-logins">
        <GoogleOAuthProvider clientId={env.googleClientId}>
          <GoogleAuth />
        </GoogleOAuthProvider>
        <TwitterAuth />
        <AppleAuth />
      </div>
    </VerticalForm>
  );
};

export default SignUpPage;
